import { useEffect, useId, useRef } from 'react';
import type { Settings } from '../settings.js';
import type { View } from '../route.js';

interface Props {
  settings: Settings;
  onChange: (patch: Partial<Settings>) => void;
  go: (view: View) => void;
}

export function SettingsPage({ settings, onChange, go }: Props) {
  const id = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => headingRef.current?.focus(), []);

  return (
    <section className="panel" aria-labelledby={`${id}-h`}>
      <h2 id={`${id}-h`} ref={headingRef} tabIndex={-1}>
        Settings
      </h2>
      <p className="muted">Saved in this browser only. They apply straight away.</p>
      <div className="settings">
        <fieldset>
          <legend>Theme</legend>
          {(
            [
              ['system', 'Match my device'],
              ['light', 'Light'],
              ['dark', 'Dark'],
            ] as const
          ).map(([value, label]) => (
            <label key={value}>
              <input
                type="radio"
                name={`${id}-theme`}
                checked={settings.theme === value}
                onChange={() => onChange({ theme: value })}
              />
              {label}
            </label>
          ))}
        </fieldset>

        <fieldset>
          <legend>Tile colours</legend>
          {(
            [
              ['default', 'Standard: green, amber, red'],
              ['colorblind', 'Colour-blind friendly: blue, orange, dark grey'],
            ] as const
          ).map(([value, label]) => (
            <label key={value}>
              <input
                type="radio"
                name={`${id}-palette`}
                checked={settings.palette === value}
                onChange={() => onChange({ palette: value })}
              />
              {label}
            </label>
          ))}
          <p className="hint">
            Every tile also has an icon and prints its value, so colour is never the only clue.
          </p>
        </fieldset>

        <fieldset>
          <legend>Motion</legend>
          <label>
            <input
              type="checkbox"
              checked={settings.reduceMotion}
              onChange={(e) => onChange({ reduceMotion: e.target.checked })}
            />
            Reduce motion (no tile flip animation)
          </label>
          <p className="hint">
            Your device&rsquo;s reduced-motion setting is always respected too.
          </p>
        </fieldset>
      </div>
      <div className="actions">
        <button type="button" className="primary" onClick={() => go('play')}>
          Back to the game
        </button>
      </div>
    </section>
  );
}
